import { DatePicker } from "@app/components/DatePicker";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useCategoryData from "@app/data-hooks/use-category-data.ts";
import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-store";
import { CurrencyInput, Dialog, QueryStatusRouter, Select } from "bi-lib";
import moment from "moment";
import { useEffect } from "react";

interface Props {
    open: boolean;
    onClose: () => void;
    id?: number;
    assignmentId: number;
}

export default function AssignmentRatesExpenseModal({ open, onClose, id, assignmentId }: Props) {
    const { expenseDetail, updateExpenseDetail } = useAssignmentData({ expenseId: open ? id : undefined });

    const defaultValues: any = {
        categoryId: 1,
        effDate: expenseDetail.data?.effectiveDate ? moment(expenseDetail.data?.effectiveDate) : null,
        subCatalogId: expenseDetail.data?.subCategoryId,
        reimbursable: expenseDetail.data?.reimbursable,
        costRt: expenseDetail.data?.costRate,
        billingRt: expenseDetail.data?.billingRate,
        internalRt: expenseDetail.data?.internalRate,
        markup: expenseDetail.data?.markupAmount,
    };

    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            await updateExpenseDetail.mutateAsync({
                id,
                assignmentId,
                effDate: value.effDate?.format("MM/DD/YYYY"),
                subCatalogId: value.subCatalogId,
                reimbursable: value.reimbursable,
                costRt: value.costRt,
                billingRt: value.billingRt,
                internalRt: value.internalRt,
                markup: value.markup,
            });
            onClose();
        },
    });

    const categoryId = useStore(form.store, (state) => state.values.categoryId);

    const { categories, subcategoryList } = useCategoryData({ entryCategoryId: categoryId });

    useEffect(() => {
        if (subcategoryList.data?.length == 1) {
            const data = subcategoryList.data[0].subcategoryId;
            form.setFieldValue("subCatalogId", data);
            return;
        }
        form.setFieldValue("subCatalogId", undefined);
    }, [subcategoryList.data]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
            <DialogTitle>{id == undefined ? "Create" : "Edit"} Expense Rate</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Stack spacing={1}>
                        <QueryStatusRouter queryHook={categories}>
                            <form.Field name={"categoryId"}>
                                {(field) => (
                                    <Select
                                        label={"Category"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e!)}
                                        options={categories.data
                                            ?.filter((x) => x.categoryId != undefined && x.categoryId <= 2)
                                            .map((x) => ({ label: x.categoryDescription, value: x.categoryId }))}
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                        <QueryStatusRouter queryHook={subcategoryList}>
                            <form.Field name={"subCatalogId"}>
                                {(field) => (
                                    <Select
                                        label={"Subcategory"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        options={subcategoryList.data?.map((x) => ({
                                            label: x.subcategoryDescription,
                                            value: x.subcategoryId,
                                        }))}
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                        <form.Field name={"effDate"}>
                            {(field) => (
                                <DatePicker
                                    label={"Effective Date"}
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"reimbursable"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            value={field.state.value ?? ""}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Reimbursable"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"costRt"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Cost Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billingRt"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Billing Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"internalRt"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Internal Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"markup"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Markup"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                    <Button type={"submit"} variant={"contained"}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
